/**
 * The pointer `show_in_viewer` draws: a pulsing outline over an existing
 * control, owned entirely by the agent (a fixed-position element appended
 * to the page). It has `pointer-events: none`, so it never intercepts a
 * click, and it never touches the control itself. It follows the control
 * while the page scrolls or resizes, disappears after 2 minutes or on
 * the next click anywhere, and respects reduced motion. For a control inside a
 * closed menu or on another workflow step it guides: it outlines the step
 * button and/or the menu button first, then moves to the control as each
 * appears.
 * One highlight at a time; a new one replaces the previous.
 */
import type { ProbeResult } from './showInViewer';

/** How long an outline stays unless the clinician clicks first (ms). */
export const HIGHLIGHT_MS = 120000;
/** How long to keep watching for the step / menu / control to appear:
 *  as long as an outline lasts (ms). */
export const FOLLOW_WAIT_MS = HIGHLIGHT_MS;
const POLL_MS = 200;
const STYLE_ID = 'agent-show-in-viewer-style';

export function isVisible(el: Element): boolean {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) {
        return false;
    }
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
}

const isDisabled = (el: Element) =>
    el.matches(':disabled, [aria-disabled="true"]') ||
    !!el.querySelector(':scope > button:disabled');

export function firstVisible(
    selector: string,
    doc: Document
): Element | undefined {
    return Array.from(doc.querySelectorAll(selector)).find(isVisible);
}

export function probeSelector(selector: string, doc: Document): ProbeResult {
    const el = firstVisible(selector, doc);
    if (!el) {
        return 'absent';
    }
    return isDisabled(el) ? 'disabled' : 'visible';
}

export interface Highlighter {
    show(chain: ReadonlyArray<string>): void;
    clear(): void;
}

export function createHighlighter(doc: Document, color: string): Highlighter {
    let box: HTMLDivElement | undefined;
    let raf = 0;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;
    let onPointerDown: ((e: Event) => void) | undefined;

    const ensureStyle = () => {
        if (doc.getElementById(STYLE_ID)) {
            return;
        }
        const s = doc.createElement('style');
        s.id = STYLE_ID;
        s.textContent =
            '@keyframes agentShowPulse{0%,100%{opacity:1}50%{opacity:.7}}' +
            `.agent-show-box{animation:agentShowPulse 1.2s ease-in-out infinite}` +
            '@media (prefers-reduced-motion: reduce){.agent-show-box{animation:none}}';
        doc.head.appendChild(s);
    };

    const removeBox = () => {
        cancelAnimationFrame(raf);
        if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = undefined;
        }
        box?.remove();
        box = undefined;
    };

    const clear = () => {
        removeBox();
        if (poll) {
            clearInterval(poll);
            poll = undefined;
        }
        if (onPointerDown) {
            doc.removeEventListener('pointerdown', onPointerDown, true);
            onPointerDown = undefined;
        }
    };

    const outline = (el: Element) => {
        removeBox();
        ensureStyle();
        el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        const b = doc.createElement('div');
        b.className = 'agent-show-box';
        b.setAttribute('aria-hidden', 'true');
        Object.assign(b.style, {
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: '2147483000',
            // Strong on the dark viewer: a thick theme-colour ring, a white
            // inner edge for contrast, a solid glow and a light tint.
            border: `3px solid ${color}`,
            borderRadius: '6px',
            backgroundColor: `${color}26`,
            boxShadow:
                `inset 0 0 0 1px #FFFFFF, 0 0 0 3px ${color}AA, ` +
                `0 0 18px 4px ${color}`,
            transition: 'top .1s, left .1s, width .1s, height .1s',
        });
        doc.body.appendChild(b);
        box = b;
        const place = () => {
            if (!el.isConnected) {
                removeBox();
                return;
            }
            const r = el.getBoundingClientRect();
            b.style.top = `${r.top - 4}px`;
            b.style.left = `${r.left - 4}px`;
            b.style.width = `${r.width + 8}px`;
            b.style.height = `${r.height + 8}px`;
            raf = requestAnimationFrame(place);
        };
        place();
        hideTimer = setTimeout(removeBox, HIGHLIGHT_MS);
    };

    /**
     * Guide along `chain` (e.g. [step button, menu button, control]):
     * outline the furthest element on screen now, then keep watching and
     * move the outline forward as later ones appear (the clinician opens
     * the step, then the menu), until the control itself is outlined or
     * the watch ends. Works even when nothing is on screen yet.
     */
    const show = (chain: ReadonlyArray<string>) => {
        clear();
        if (chain.length === 0) {
            return;
        }
        const last = chain.length - 1;
        const started = Date.now();
        let reached = -1;
        const tick = () => {
            for (let i = last; i > reached; i--) {
                const el = firstVisible(chain[i], doc);
                if (el) {
                    outline(el);
                    reached = i;
                    break;
                }
            }
            if (
                (reached === last || Date.now() - started > FOLLOW_WAIT_MS) &&
                poll
            ) {
                clearInterval(poll);
                poll = undefined;
            }
        };
        // The next click ends the current outline (the clinician is
        // acting); watching for the next element continues.
        onPointerDown = () => removeBox();
        doc.addEventListener('pointerdown', onPointerDown, true);
        poll = setInterval(tick, POLL_MS);
        tick();
    };

    return { show, clear };
}
